import { Alert, Box, Card, CardContent, styled, Typography } from '@ethings/ui-toolkit';
import { ReactNode, RefObject, useEffect, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponsiveText } from '../../hooks';
import { innerText } from '../../util';
import { MiddleSpinner } from '../middle-spinner';
import { MicroPortal } from './micro-portal';
import { MicroPortalMessageType, MicroPortalProps } from './micro-portal.types';

interface ResponsiveMicroPortalCardProps {
  title?: ReactNode;
  microPortalProps: Omit<MicroPortalProps, 'iframeRef' | 'errorFallback' | 'loadingFallback' | 'height'>;
  errorMessage?: ReactNode;
  testId?: string;
}

const DEFAULT_HEIGHT = 300;

const StyledCard = styled(Card)({
  overflow: 'hidden',
});

const LoadingBox = styled(Box)(({ theme }) => ({
  margin: theme.spacing(4),
}));

const ErrorBox = styled(Box)(({ theme }) => ({
  margin: theme.spacing(2),
}));

export const ResponsiveMicroPortalCard: FC<ResponsiveMicroPortalCardProps> = ({ title, microPortalProps, errorMessage, testId }) => {
  const { iframeRef, height } = useResponsiveMicroPortalHeight();
  const { t } = useTranslation(['common']);

  const titleLength = title ? (typeof title === 'string' ? title.length : innerText(title).length) : 0;
  const h5Styles = useResponsiveText('1.5rem', titleLength, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  return (
    <StyledCard data-testid={testId}>
      {title && (
        <CardContent>
          <Typography
            variant="h5"
            style={{
              fontSize: h5Styles.fontSize,
              lineHeight: h5Styles.lineHeight,
              wordBreak: h5Styles.wordBreak,
              whiteSpace: h5Styles.whiteSpace,
              overflow: h5Styles.overflow,
              textOverflow: h5Styles.textOverflow,
            }}
          >
            {title}
          </Typography>
        </CardContent>
      )}
      <div>
        <MicroPortal
          height={height}
          iframeRef={iframeRef}
          loadingFallback={(
            <LoadingBox>
              <MiddleSpinner />
            </LoadingBox>
          )}
          errorFallback={() => (
            <ErrorBox>
              <Alert severity="error">
                {errorMessage ?? t('common:common.hint.issue')}
              </Alert>
            </ErrorBox>
          )}
          {...microPortalProps}
        />
      </div>
    </StyledCard>
  );
};

const useResponsiveMicroPortalHeight = (): {
  iframeRef: RefObject<HTMLIFrameElement | undefined>;
  height: number;
} => {
  const iframeRef = useRef<HTMLIFrameElement | undefined>(undefined);
  const [height, setHeight] = useState<number>(DEFAULT_HEIGHT);

  useEffect(() => {
    const onMessage = (event: MessageEvent<{ type?: string }>): void => {
      try {
        if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
          return;
        }
        if (!event.data.type) {
          return;
        }
        if (event.data.type === MicroPortalMessageType.RESIZE && 'height' in event.data && typeof event.data.height === 'number') {
          setHeight(event.data.height);
        }
      }
      catch (error) {
        console.error(error);
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [iframeRef]);

  return {
    iframeRef,
    height,
  };
};
